export type DenominationType = "note" | "coin";

export interface CashDenomination {
  value: number;
  label: string;
  type: DenominationType;
}

export type CashDenominationCounts = Record<string, number>;

export const EUR_CASH_DENOMINATIONS: CashDenomination[] = [
  { value: 500, label: "500 €", type: "note" },
  { value: 200, label: "200 €", type: "note" },
  { value: 100, label: "100 €", type: "note" },
  { value: 50, label: "50 €", type: "note" },
  { value: 20, label: "20 €", type: "note" },
  { value: 10, label: "10 €", type: "note" },
  { value: 5, label: "5 €", type: "note" },
  { value: 2, label: "2 €", type: "coin" },
  { value: 1, label: "1 €", type: "coin" },
  { value: 0.5, label: "50 ct", type: "coin" },
  { value: 0.2, label: "20 ct", type: "coin" },
  { value: 0.1, label: "10 ct", type: "coin" },
  { value: 0.05, label: "5 ct", type: "coin" },
  { value: 0.02, label: "2 ct", type: "coin" },
  { value: 0.01, label: "1 ct", type: "coin" },
];

export const EUR_NOTE_DENOMINATIONS = EUR_CASH_DENOMINATIONS.filter((d) => d.type === "note");
export const EUR_COIN_DENOMINATIONS = EUR_CASH_DENOMINATIONS.filter((d) => d.type === "coin");

export function denominationKey(value: number): string {
  return value.toFixed(2);
}

export function emptyDenominationCounts(): CashDenominationCounts {
  return Object.fromEntries(EUR_CASH_DENOMINATIONS.map((d) => [denominationKey(d.value), 0]));
}

export function sumDenominationCounts(counts: CashDenominationCounts): number {
  let total = 0;
  for (const denomination of EUR_CASH_DENOMINATIONS) {
    const count = counts[denominationKey(denomination.value)] ?? 0;
    if (count > 0) {
      total += denomination.value * count;
    }
  }
  return Math.round(total * 100) / 100;
}

export function hasDenominationCounts(counts: CashDenominationCounts): boolean {
  return EUR_CASH_DENOMINATIONS.some(
    (d) => (counts[denominationKey(d.value)] ?? 0) > 0,
  );
}

export function normalizeDenominationCounts(
  counts: Record<string, unknown> | null | undefined,
): CashDenominationCounts {
  const normalized = emptyDenominationCounts();
  if (!counts) return normalized;
  for (const denomination of EUR_CASH_DENOMINATIONS) {
    const key = denominationKey(denomination.value);
    const raw = counts[key];
    const parsed = typeof raw === "number" ? raw : Number(raw);
    normalized[key] = Number.isFinite(parsed) && parsed > 0 ? Math.floor(parsed) : 0;
  }
  return normalized;
}

/** Optimale Euro-Rückgeldstückelung (greedy, größte Stückelung zuerst). */
export function computeChangeBreakdown(changeAmount: number): CashDenominationCounts {
  const counts = emptyDenominationCounts();
  if (changeAmount <= 0) return counts;

  let remaining = Math.round(changeAmount * 100) / 100;
  for (const denomination of EUR_CASH_DENOMINATIONS) {
    if (remaining <= 0) break;
    const count = Math.floor((remaining + 1e-9) / denomination.value);
    if (count > 0) {
      counts[denominationKey(denomination.value)] = count;
      remaining = Math.round((remaining - count * denomination.value) * 100) / 100;
    }
  }
  return counts;
}

export function formatChangeBreakdown(counts: CashDenominationCounts): string[] {
  const lines: string[] = [];
  for (const denomination of EUR_CASH_DENOMINATIONS) {
    const count = counts[denominationKey(denomination.value)] ?? 0;
    if (count > 0) {
      lines.push(`${count}× ${denomination.label}`);
    }
  }
  return lines;
}
